import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";

// toast notifications
import { toast } from "react-toastify";

// @mui material components
import Card from "@mui/material/Card";
import MenuItem from "@mui/material/MenuItem";
import Skeleton from "@mui/material/Skeleton";

// Material Dashboard 2 PRO React TS components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 PRO React TS examples components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";
import BtnMenu from "./components/BtnMenu";

// Data
import { useListUsers, useDeleteUser, useUpdateBasicInfo } from "api/features/auth/queries";
import { getQueryErrorMessage } from "api/utils/queryError";
import { PublicUser } from "@/dashboard-api-types/api/auth.types";

import { getDataTableUserData, getDataTableRequestData } from "./data/dataTableData";

import { useUserContext } from "contexts/contextUser";

function ManageUsers(): JSX.Element {
  const [dataTableDataState, setDataTableDataState] = useState(getDataTableUserData([]));
  const [requestTableDataState, setRequestTableDataState] = useState(getDataTableRequestData([]));
  const [selectedRows, setSelectedRows] = useState<PublicUser[]>([]);
  const [selectedRequests, setSelectedRequests] = useState<PublicUser[]>([]);

  const { user } = useUserContext();
  const listUsersClient = useListUsers();
  const deleteUserClient = useDeleteUser();
  const updateBasicInfoClient = useUpdateBasicInfo();

  const navigation = useNavigate();

  const handleCreate = () => navigation("/administration/new-user");

  const { data: listUsersData, isLoading: isLoadingUserData } = listUsersClient;

  const pendingUsers = useMemo(
    () => (listUsersData ?? []).filter((u) => u.user_status === "pending_verification"),
    [listUsersData]
  );
  const managedUsers = useMemo(
    () => (listUsersData ?? []).filter((u) => u.user_status !== "pending_verification"),
    [listUsersData]
  );

  const handleDelete = () => {
    selectedRows.forEach(({ user_id }) => {
      deleteUserClient.mutate(
        { userId: user_id },
        {
          onSuccess: () => toast.success("User disabled successfully"),
          onError: (error: Error) => toast.error(`Error disabling user: ${error}`),
        }
      );
    });
  };

  const handlePromoteToAdmin = () => {
    const target = selectedRows[0];
    if (!target?.user_id) {
      toast.error("Select a user first");
      return;
    }
    if (target.user_role?.toUpperCase() !== "USER") {
      toast.error("Only a USER can be promoted to admin");
      return;
    }

    updateBasicInfoClient.mutate(
      { userId: target.user_id, data: { role: "ADMIN" } },
      {
        onSuccess: () => {
          toast.success("User promoted to admin");
          setSelectedRows([]);
        },
        onError: (error) => toast.error(getQueryErrorMessage(error)),
      }
    );
  };

  const handleAcceptRequests = async () => {
    try {
      await Promise.all(
        selectedRequests.map(({ user_id }) =>
          updateBasicInfoClient.mutateAsync({ userId: user_id, data: { status: "active" } })
        )
      );
      toast.success(selectedRequests.length > 1 ? "Requests accepted" : "Request accepted");
      setSelectedRequests([]);
    } catch (error) {
      toast.error(getQueryErrorMessage(error));
    }
  };

  const handleRefuseRequests = async () => {
    try {
      await Promise.all(
        selectedRequests.map(({ user_id }) => deleteUserClient.mutateAsync({ userId: user_id }))
      );
      toast.success(selectedRequests.length > 1 ? "Requests refused" : "Request refused");
      setSelectedRequests([]);
    } catch (error) {
      toast.error(getQueryErrorMessage(error));
    }
  };

  useEffect(() => {
    setDataTableDataState(getDataTableUserData(managedUsers, user?.user_email));
  }, [managedUsers, user]);

  useEffect(() => {
    setRequestTableDataState(getDataTableRequestData(pendingUsers));
  }, [pendingUsers]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox my={3}>
        <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <MDTypography variant="h6">Users</MDTypography>
          <MDBox display="flex" gap={1}>
            <MDButton variant="gradient" color="info" onClick={handleCreate}>
              create user
            </MDButton>
            <BtnMenu text="actions" disabled={!selectedRows.length}>
              <MenuItem
                onClick={handlePromoteToAdmin}
                disabled={selectedRows[0]?.user_role?.toUpperCase() !== "USER"}
              >
                Promote to admin
              </MenuItem>
              <MenuItem
                onClick={handleDelete}
                disabled={selectedRows[0]?.user_role?.toUpperCase() === "ADMIN"}
              >
                Delete
              </MenuItem>
            </BtnMenu>
          </MDBox>
        </MDBox>

        <Card>
          {isLoadingUserData ? (
            <MDBox p={2}>
              <Skeleton height={80} />
              <Skeleton height={50} />
              <Skeleton height={50} />
              <Skeleton height={50} />
            </MDBox>
          ) : (
            <DataTable
              table={dataTableDataState}
              entriesPerPage={false}
              canSearch
              setSelectedRows={setSelectedRows}
            />
          )}
        </Card>

        <MDBox display="flex" justifyContent="space-between" alignItems="center" mt={6} mb={2}>
          <MDTypography variant="h6">Registration requests</MDTypography>
          <BtnMenu text="actions" disabled={!selectedRequests.length}>
            <MenuItem onClick={handleAcceptRequests}>Accept</MenuItem>
            <MenuItem onClick={handleRefuseRequests}>Refuse</MenuItem>
          </BtnMenu>
        </MDBox>

        <Card>
          {isLoadingUserData ? (
            <MDBox p={2}>
              <Skeleton height={80} />
              <Skeleton height={50} />
              <Skeleton height={50} />
            </MDBox>
          ) : pendingUsers.length === 0 ? (
            <MDBox p={3}>
              <MDTypography variant="button" color="text">
                No pending registration requests.
              </MDTypography>
            </MDBox>
          ) : (
            <DataTable
              table={requestTableDataState}
              entriesPerPage={false}
              canSearch
              setSelectedRows={setSelectedRequests}
            />
          )}
        </Card>
      </MDBox>
    </DashboardLayout>
  );
}

export default ManageUsers;
